import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectDetailModal from './components/ProjectDetailModal';
import CreateProjectModal from './components/CreateProjectModal';
import ProjectStatusBadge from './components/ProjectStatusBadge';
import ProjectProgressBar from './components/ProjectProgressBar';
import { projectService } from '../../services/projectService';
import { resourceService } from '../../services/resourceService';
import { supabase } from '../../lib/supabase';
import { parseLocalDate } from '../../utils/dateUtils';
import { createRealtimeSubscription, RealtimeErrorType } from '../../utils/realtimeErrorHandler';
import { useAuth } from '../../contexts/AuthContext';
import PresenceBadges from '../../components/PresenceBadges';
import { joinPresenceChannel } from '../../services/presenceService';

const ALL_COLUMNS = [
{ key: 'projectNumber', label: 'Project #', sortable: true, required: true },
{ key: 'projectName', label: 'Project Name', sortable: true },
{ key: 'customerName', label: 'Customer', sortable: true },
{ key: 'projectManager', label: 'Project Manager', sortable: true },
{ key: 'status', label: 'Status', sortable: true },
{ key: 'progress', label: 'Progress', sortable: true },
{ key: 'budget', label: 'Budget', sortable: true },
{ key: 'startDate', label: 'Start Date', sortable: true },
{ key: 'actions', label: 'Actions', sortable: false, required: true }];


const DEFAULT_VISIBLE = ['projectNumber', 'projectName', 'customerName', 'projectManager', 'status', 'progress', 'budget', 'startDate', 'actions'];
const DEFAULT_FILTERS = { search: '', status: 'exclude_complete', phase: 'all', pm: 'all', customer: 'all' };
const DEFAULT_SORT = { column: 'projectNumber', direction: 'desc' };

const ProjectManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const isAdmin = userProfile?.role === 'administrator' || userProfile?.role === 'global_administrator';

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeError, setRealtimeError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Presence state: map of projectId -> presenceList
  const [projectPresence, setProjectPresence] = useState({});
  const presenceChannelsRef = useRef({});

  // Table preferences
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [pmUsers, setPmUsers] = useState([]);
  const columnPickerRef = useRef(null);

  // Modal state
  const [selectedProject, setSelectedProject] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [convertingBid, setConvertingBid] = useState(null);
  const [editingProject, setEditingProject] = useState(null);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { projectId, field }
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const editInputRef = useRef(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // project object
  const [deleting, setDeleting] = useState(false);

  // Archive confirmation state
  const [archiveConfirm, setArchiveConfirm] = useState(null); // project object
  const [archiving, setArchiving] = useState(false);
  const [archiveUnassignResources, setArchiveUnassignResources] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Contractor stats for summary box
  const [contractorStats, setContractorStats] = useState({ total: 0, gc: 0, sc: 0, v: 0 });

  const filteredAndSortedProjects = useMemo(() => {
    let result = [...projects];

    if (filters?.search) {
      const q = filters?.search?.toLowerCase();
      result = result?.filter((p) =>
      p?.projectNumber?.toLowerCase()?.includes(q) ||
      p?.projectName?.toLowerCase()?.includes(q) ||
      p?.customerName?.toLowerCase()?.includes(q)
      );
    }

    if (filters?.status !== 'all') {
      if (filters?.status === 'exclude_complete') {
        result = result?.filter((p) => p?.status !== 'Completed' && p?.status !== 'Cancelled');
      } else {
        result = result?.filter((p) => p?.status === filters?.status);
      }
    }

    if (filters?.phase !== 'all') {
      result = result?.filter((p) =>
      p?.phases?.some((phase) => phase?.status === filters?.phase)
      );
    }

    if (filters?.pm !== 'all') {
      result = result?.filter((p) => p?.assignedPmId === filters?.pm);
    }

    if (filters?.customer !== 'all') {
      result = result?.filter((p) => p?.customerName === filters?.customer);
    }

    result?.sort((a, b) => {
      let aVal, bVal;
      switch (sort?.column) {
        case 'projectNumber':aVal = a?.projectNumber || '';bVal = b?.projectNumber || '';break;
        case 'projectName':aVal = a?.projectName || '';bVal = b?.projectName || '';break;
        case 'customerName':aVal = a?.customerName || '';bVal = b?.customerName || '';break;
        case 'projectManager':aVal = (a?.assignedPm?.nickname || a?.assignedPm?.fullName || a?.assignedPm?.email || '');bVal = (b?.assignedPm?.nickname || b?.assignedPm?.fullName || b?.assignedPm?.email || '');break;
        case 'status':aVal = a?.status || '';bVal = b?.status || '';break;
        case 'progress':aVal = a?.completionPercentage || 0;bVal = b?.completionPercentage || 0;break;
        case 'budget':aVal = parseFloat(a?.totalBudget) || 0;bVal = parseFloat(b?.totalBudget) || 0;break;
        case 'startDate':aVal = parseLocalDate(a?.startDate);bVal = parseLocalDate(b?.startDate);break;
        default:aVal = a?.projectNumber || '';bVal = b?.projectNumber || '';
      }
      if (typeof aVal === 'string') {
        return sort?.direction === 'asc' ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      }
      return sort?.direction === 'asc' ? aVal < bVal ? -1 : aVal > bVal ? 1 : 0 : bVal < aVal ? -1 : bVal > aVal ? 1 : 0;
    });

    return result;
  }, [projects, filters, sort]);

  const stats = useMemo(() => {
    const activeProjects = projects?.filter((p) => p?.status !== 'Completed' && p?.status !== 'Cancelled');
    const totalProjects = activeProjects?.length || 0;
    const inProgress = projects?.filter((p) => p?.status === 'In Progress')?.length || 0;
    const planning = projects?.filter((p) => p?.status === 'Planning')?.length || 0;
    const onHold = projects?.filter((p) => p?.status === 'On Hold')?.length || 0;
    const totalBudget = activeProjects?.reduce((sum, p) => sum + (p?.totalBudget || 0), 0);
    return { totalProjects, inProgress, planning, onHold, totalBudget };
  }, [projects]);

  const uniqueCustomers = useMemo(() => {
    const names = [...new Set(projects?.map((p) => p?.customerName)?.filter(Boolean))];
    return names?.sort();
  }, [projects]);

  const activeColumns = ALL_COLUMNS?.filter((c) => visibleColumns?.includes(c?.key));
  const hasActiveFilters = filters?.search || filters?.status !== 'all' && filters?.status !== 'exclude_complete' || filters?.phase !== 'all' || filters?.pm !== 'all' || filters?.customer !== 'all';

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(amount);
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = String(dateString)?.split('T')?.[0]?.split('-');
    if (parts?.length !== 3) return dateString;
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  };

  const SortIcon = ({ colKey }) => {
    if (sort?.column !== colKey) return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground/50 ml-1" />;
    return sort?.direction === 'asc' ?
    <Icon name="ChevronUp" size={14} className="text-accent ml-1" /> :
    <Icon name="ChevronDown" size={14} className="text-accent ml-1" />;
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredAndSortedProjects?.length / pageSize)), [filteredAndSortedProjects, pageSize]);
  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedProjects?.slice(start, start + pageSize);
  }, [filteredAndSortedProjects, currentPage, pageSize]);

  useEffect(() => {
    loadProjects();
    loadPreferences();
    loadPMUsers();
    loadContractorStats();

    if (location?.state?.convertFromBid) {
      setConvertingBid(location?.state?.convertFromBid);
      setShowCreateModal(true);
      window.history?.replaceState({}, document.title);
    }

    const handleError = (err) => {
      setRealtimeError(err);
      setIsRetrying(err?.willRetry || false);
      if (!err?.willRetry && err?.type !== RealtimeErrorType?.CONNECTION_FAILED) {
        setTimeout(() => setRealtimeError(null), 5000);
      }
    };

    const { cleanup } = createRealtimeSubscription({
      supabase,
      channelName: 'projects_changes',
      tableName: 'projects',
      onDataChange: () => {
        loadProjects();
        setRealtimeError(null);
        setIsRetrying(false);
      },
      onError: handleError
    });

    return cleanup;
  }, []);

  // ── Presence: subscribe to each visible project's presence channel ──────
  useEffect(() => {
    if (!userProfile || paginatedProjects?.length === 0) return;

    const currentIds = new Set(paginatedProjects?.map((p) => p?.id));
    const existingIds = new Set(Object.keys(presenceChannelsRef?.current));

    // Leave channels for projects no longer visible
    for (const pid of existingIds) {
      if (!currentIds?.has(pid)) {
        presenceChannelsRef?.current?.[pid]?.leave?.();
        delete presenceChannelsRef?.current?.[pid];
      }
    }

    // Join channels for newly visible projects
    for (const project of paginatedProjects) {
      if (!existingIds?.has(project?.id)) {
        const { leave } = joinPresenceChannel(
          `project:${project?.id}`,
          userProfile,
          (list) => {
            setProjectPresence((prev) => ({ ...prev, [project?.id]: list }));
          }
        );
        presenceChannelsRef.current[project?.id] = { leave };
      }
    }

    return () => {
      // Cleanup on unmount
      for (const pid of Object.keys(presenceChannelsRef?.current)) {
        presenceChannelsRef?.current?.[pid]?.leave?.();
      }
      presenceChannelsRef.current = {};
    };
  }, [paginatedProjects, userProfile]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnPickerRef?.current && !columnPickerRef?.current?.contains(e?.target)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      const { data, error: prefError } = await supabase?.
      from('user_project_table_preferences')?.
      select('*')?.
      eq('user_id', user?.id)?.
      single();
      if (data && !prefError) {
        setVisibleColumns(data?.visible_columns || DEFAULT_VISIBLE);
        setFilters(data?.saved_filters || DEFAULT_FILTERS);
        setSort({ column: data?.sort_column || 'projectNumber', direction: data?.sort_direction || 'desc' });
      }
    } catch (err) {

      // Use defaults
    } finally {setPrefsLoaded(true);
    }
  };

  const savePreferences = async (newVisible, newFilters, newSort) => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      await supabase?.
      from('user_project_table_preferences')?.
      upsert({
        user_id: user?.id,
        visible_columns: newVisible,
        saved_filters: newFilters,
        sort_column: newSort?.column,
        sort_direction: newSort?.direction
      }, { onConflict: 'user_id' });
    } catch (err) {
      console.error('Failed to save project preferences:', err?.message);
    }
  };

  const loadPMUsers = async () => {
    try {
      const { data } = await projectService?.getPMUsers();
      setPmUsers(data || []);
    } catch (err) {

      // silently fail
    }};

  const loadProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await projectService?.getAll();
      if (fetchError) throw fetchError;
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err?.message);
      setError(err?.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadContractorStats = async () => {
    try {
      const [gcRes, scRes, vRes] = await Promise.all([
        supabase?.from('general_contractors')?.select('id', { count: 'exact', head: true }),
        supabase?.from('sub_contractors')?.select('id', { count: 'exact', head: true }),
        supabase?.from('vendors')?.select('id', { count: 'exact', head: true }),
      ]);
      const gc = gcRes?.count || 0;
      const sc = scRes?.count || 0;
      const v = vRes?.count || 0;
      setContractorStats({ total: gc + sc + v, gc, sc, v });
    } catch (err) {
      console.error('Error loading contractor stats:', err?.message);
    }
  };

  const handleCreateProject = async (projectData) => {
    try {
      const { data, error: createError } = await projectService?.create(projectData);
      if (createError) throw createError;
      await loadProjects();
      setShowCreateModal(false);
      return data;
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateProject = async (id, projectData) => {
    try {
      const { data, error: updateError } = await projectService?.update(id, projectData);
      if (updateError) throw updateError;
      await loadProjects();
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      const { error: deleteError } = await projectService?.delete(id);
      if (deleteError) throw deleteError;
      await loadProjects();
      setSelectedProject(null);
    } catch (err) {
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await handleDeleteProject(deleteConfirm?.id);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete error:', err?.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleArchiveProject = async (project) => {
    try {
      const { error } = await projectService?.archive(project?.id, project?.status);
      if (error) throw error;
      await loadProjects();
    } catch (err) {
      console.error('Archive error:', err?.message);
    }
  };

  const handleConfirmArchive = async () => {
    if (!archiveConfirm) return;
    setArchiving(true);
    try {
      if (archiveUnassignResources) {
        await resourceService?.unassignAllFromProject(archiveConfirm?.id);
      }
      await handleArchiveProject(archiveConfirm);
      setArchiveConfirm(null);
      setArchiveUnassignResources(false);
    } catch (err) {
      console.error('Archive error:', err?.message);
    } finally {
      setArchiving(false);
    }
  };

  const handleSortChange = (colKey) => {
    const col = ALL_COLUMNS?.find((c) => c?.key === colKey);
    if (!col?.sortable) return;
    const newSort = sort?.column === colKey ?
    { column: colKey, direction: sort?.direction === 'asc' ? 'desc' : 'asc' } :
    { column: colKey, direction: 'asc' };
    setSort(newSort);
    savePreferences(visibleColumns, filters, newSort);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    savePreferences(visibleColumns, newFilters, sort);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
    savePreferences(visibleColumns, DEFAULT_FILTERS, sort);
  };

  const handleToggleColumn = (colKey) => {
    const col = ALL_COLUMNS?.find((c) => c?.key === colKey);
    if (col?.required) return;
    const newVisible = visibleColumns?.includes(colKey) ?
    visibleColumns?.filter((k) => k !== colKey) :
    [...visibleColumns, colKey];
    setVisibleColumns(newVisible);
    savePreferences(newVisible, filters, sort);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setSelectedProject(null);
    setShowCreateModal(true);
  };

  const handleInlineEdit = useCallback((project, field) => {
    let initialValue = '';
    switch (field) {
      case 'projectName':initialValue = project?.projectName || '';break;
      case 'status':initialValue = project?.status || 'Planning';break;
      case 'budget':initialValue = project?.totalBudget || '';break;
      case 'startDate':initialValue = project?.startDate ? project?.startDate?.split('T')?.[0] : '';break;
      case 'assignedPm':initialValue = project?.assignedPmId || project?.assigned_pm_id || '';break;
      default:return;
    }
    setEditingCell({ projectId: project?.id, field });
    setEditingValue(initialValue);
  }, []);

  const handleInlineSave = useCallback(async (project, field) => {
    const cellKey = `${project?.id}-${field}`;
    setSavingCell(cellKey);
    try {
      let updatePayload = {};
      switch (field) {
        case 'projectName':
          if (!editingValue?.trim()) {setEditingCell(null);return;}
          updatePayload = { projectName: editingValue?.trim() };
          break;
        case 'status':
          updatePayload = { status: editingValue };
          break;
        case 'budget':
          const budgetNum = parseFloat(editingValue);
          if (isNaN(budgetNum)) {setEditingCell(null);return;}
          updatePayload = { totalBudget: budgetNum };
          break;
        case 'startDate':
          if (!editingValue) {setEditingCell(null);return;}
          updatePayload = { startDate: editingValue };
          break;
        case 'assignedPm':
          updatePayload = { assignedPmId: editingValue || null };
          break;
        default:break;
      }
      if (field === 'status' && editingValue === 'Cancelled') {
        await projectService?.cancel(project?.id, project?.status);
        await loadProjects();
      } else {
        await handleUpdateProject(project?.id, updatePayload);
      }
    } catch (err) {
      console.error('Inline save error:', err?.message);
    } finally {
      setSavingCell(null);
      setEditingCell(null);
    }
  }, [editingValue]);

  const handleInlineKeyDown = useCallback((e, project, field) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handleInlineSave(project, field);
    } else if (e?.key === 'Escape') {
      setEditingCell(null);
      setEditingValue('');
    }
  }, [handleInlineSave]);

  useEffect(() => {
    if (editingCell && editInputRef?.current) {
      editInputRef?.current?.focus();
      if (editInputRef?.current?.select) editInputRef?.current?.select();
    }
  }, [editingCell]);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Project Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Track and manage construction projects with real-time progress monitoring and resource allocation
            </p>
          </div>
          <Button
            variant="default"
            size="lg"
            onClick={() => setShowCreateModal(true)}
            iconName="Plus"
            iconPosition="left"
            iconSize={20}>

            Create Project
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="bg-card rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Icon name="FolderKanban" size={14} className="text-accent" />
              </div>
              <span className="text-xs font-medium text-muted-foreground truncate">Active & On-Deck</span>
            </div>
            <p className="text-2xl font-bold text-foreground leading-none mb-1">{stats?.totalProjects}</p>
            <p className="text-xs text-muted-foreground">Active & On-Deck Projects</p>
          </div>
          <div className="bg-card rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-md bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Clock" size={14} className="text-blue-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground truncate">In Progress</span>
            </div>
            <p className="text-2xl font-bold text-foreground leading-none mb-1">{stats?.inProgress}</p>
            <p className="text-xs text-muted-foreground">In Progress</p>
          </div>
          <div className="bg-card rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-md bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Icon name="ClipboardList" size={14} className="text-green-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground truncate">Planning</span>
            </div>
            <p className="text-2xl font-bold text-foreground leading-none mb-1">{stats?.planning}</p>
            <p className="text-xs text-muted-foreground">Planning</p>
          </div>
          <div className="bg-card rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-md bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                <Icon name="PauseCircle" size={14} className="text-yellow-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground truncate">On Hold</span>
            </div>
            <p className="text-2xl font-bold text-foreground leading-none mb-1">{stats?.onHold}</p>
            <p className="text-xs text-muted-foreground">On Hold</p>
          </div>
          <div className="bg-card rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Icon name="DollarSign" size={14} className="text-accent" />
              </div>
              <span className="text-xs font-medium text-muted-foreground truncate">Total Value</span>
            </div>
            <p className="text-xl font-bold text-foreground leading-none mb-1">
              {formatCurrency(stats?.totalBudget)}
            </p>
            <p className="text-xs text-muted-foreground">Active & On-Deck Budget</p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search projects..."
                value={filters?.search}
                onChange={(e) => handleFilterChange('search', e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent" />

            </div>
            {/* Status Filter */}
            <select
              value={filters?.status}
              onChange={(e) => handleFilterChange('status', e?.target?.value)}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ width: '15.625%', minWidth: '160px' }}>

              <option value="all">All Statuses</option>
              <option value="exclude_complete">All (Except Completed)</option>
              <option value="Planning">Planning</option>
              <option value="Bidding">Bidding</option>
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            {/* PM Filter */}
            <select
              value={filters?.pm}
              onChange={(e) => handleFilterChange('pm', e?.target?.value)}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ width: '15.625%', minWidth: '160px' }}>

              <option value="all">All PMs</option>
              {pmUsers?.map((pm) =>
              <option key={pm?.id} value={pm?.id}>
                  {pm?.nickname || pm?.full_name || pm?.email}
                </option>
              )}
            </select>
            {/* Customer Filter */}
            <select
              value={filters?.customer}
              onChange={(e) => handleFilterChange('customer', e?.target?.value)}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent">

              <option value="all">All Customers</option>
              {uniqueCustomers?.map((name) =>
              <option key={name} value={name}>{name}</option>
              )}
            </select>
            {/* Phase Filter */}
            <select
              value={filters?.phase}
              onChange={(e) => handleFilterChange('phase', e?.target?.value)}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent">

              <option value="all">All Phases</option>
              <option value="Site Preparation">Site Preparation</option>
              <option value="Foundation">Foundation</option>
              <option value="Framing">Framing</option>
              <option value="Electrical">Electrical</option>
              <option value="Plumbing">Plumbing</option>
              <option value="Finishing">Finishing</option>
            </select>
            {/* Clear Filters */}
            {hasActiveFilters &&
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors whitespace-nowrap">

                <Icon name="X" size={14} />
                Clear
              </button>
            }
            {/* Column Picker */}
            <div className="relative" ref={columnPickerRef}>
              <button
                onClick={() => setShowColumnPicker((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg bg-background hover:bg-muted/50 text-foreground transition-colors whitespace-nowrap">

                <Icon name="Columns" size={14} />
                Columns
                <span className="ml-1 text-xs bg-accent/10 text-accent rounded px-1">
                  {visibleColumns?.filter((k) => k !== 'actions')?.length}
                </span>
              </button>
              {showColumnPicker &&
              <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-lg p-3 min-w-[180px]">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">Show Columns</p>
                  {ALL_COLUMNS?.filter((c) => c?.key !== 'actions')?.map((col) =>
                <label key={col?.key} className={`flex items-center gap-2 px-1 py-1.5 rounded cursor-pointer hover:bg-muted/50 ${col?.required ? 'opacity-60 cursor-not-allowed' : ''}`}>
                      <input
                    type="checkbox"
                    checked={visibleColumns?.includes(col?.key)}
                    onChange={() => handleToggleColumn(col?.key)}
                    disabled={col?.required}
                    className="rounded border-border text-accent focus:ring-accent/30" />

                      <span className="text-sm text-foreground">{col?.label}</span>
                    </label>
                )}
                </div>
              }
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-sm md:text-base text-muted-foreground">
            Showing <span className="font-semibold text-foreground">{filteredAndSortedProjects?.length}</span> project{filteredAndSortedProjects?.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Desktop Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  {activeColumns?.map((col) =>
                  <th key={col?.key} className="px-4 py-4 md:px-6 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {col?.sortable ?
                    <button
                      onClick={() => handleSortChange(col?.key)}
                      className="flex items-center hover:text-foreground transition-colors">

                          {col?.label}
                          <SortIcon colKey={col?.key} />
                        </button> :

                    col?.label
                    }
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ?
                <tr>
                    <td colSpan={activeColumns?.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                        <p className="text-sm text-muted-foreground">Loading projects...</p>
                      </div>
                    </td>
                  </tr> :
                error ?
                <tr>
                    <td colSpan={activeColumns?.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="AlertCircle" size={48} className="text-red-500" />
                        <p className="text-base text-muted-foreground">{error}</p>
                      </div>
                    </td>
                  </tr> :
                paginatedProjects?.length > 0 ?
                paginatedProjects?.map((project) =>
                <tr key={project?.id} className="border-b border-border hover:bg-muted/50 transition-colors group">
                      {activeColumns?.map((col) =>
                  <td key={col?.key} className="px-4 py-4 md:px-6">
                          {col?.key === 'projectNumber' &&
                    <div className="flex items-center gap-1.5">
                              {project?.convertedFromBid &&
                      <Icon name="FileText" size={14} className="text-accent flex-shrink-0" />
                      }
                              <span
                      className="text-sm font-medium text-foreground data-text cursor-pointer hover:text-primary hover:underline"
                      title="Double-click to edit project"
                      onDoubleClick={() => navigate(`/project-edit/${project?.id}`)}
                    >{project?.projectNumber}</span>
                            </div>
                    }
                          {col?.key === 'projectName' && (
                    editingCell?.projectId === project?.id && editingCell?.field === 'projectName' ?
                    <div className="flex items-center gap-1">
                                <input
                        ref={editInputRef}
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e?.target?.value)}
                        onBlur={() => handleInlineSave(project, 'projectName')}
                        onKeyDown={(e) => handleInlineKeyDown(e, project, 'projectName')}
                        className="text-sm font-medium text-foreground bg-background border border-accent rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-accent/30 w-full min-w-[140px]" />

                                {savingCell === `${project?.id}-projectName` &&
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-accent flex-shrink-0" />
                      }
                              </div> :

                    <div className="flex flex-col gap-0.5">
                      <div
                        className="flex items-center gap-1 group/cell cursor-pointer"
                        onClick={() => handleInlineEdit(project, 'projectName')}
                        title="Click to edit">
                        <span className="text-sm font-medium text-foreground">{project?.projectName}</span>
                        <Icon name="Pencil" size={12} className="text-muted-foreground opacity-0 group-hover/cell:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                      {projectPresence?.[project?.id]?.length > 0 &&
                        <PresenceBadges
                          presenceList={projectPresence?.[project?.id]}
                          currentUserId={userProfile?.id}
                          label="viewing"
                        />
                      }
                    </div>)

                    }
                          {col?.key === 'customerName' &&
                    <span className="text-sm text-foreground">{project?.customerName}</span>
                    }
                          {col?.key === 'projectManager' &&
                    (editingCell?.projectId === project?.id && editingCell?.field === 'assignedPm' ?
                    <div className="flex items-center gap-1">
                                <select
                        ref={editInputRef}
                        value={editingValue}
                        onChange={(e) => setEditingValue(e?.target?.value)}
                        onBlur={() => handleInlineSave(project, 'assignedPm')}
                        onKeyDown={(e) => handleInlineKeyDown(e, project, 'assignedPm')}
                        className="text-sm text-foreground bg-background border border-accent rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-accent/30 min-w-[140px]">

                                  <option value="">— Unassigned —</option>
                                  {pmUsers?.map((pm) =>
                        <option key={pm?.id} value={pm?.id}>
                                      {pm?.nickname || pm?.full_name || pm?.email}
                                    </option>
                        )}
                                </select>
                                {savingCell === `${project?.id}-assignedPm` &&
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-accent flex-shrink-0" />
                      }
                              </div> :

                    <div
                      className="flex items-center gap-1 group/cell cursor-pointer"
                      onClick={() => handleInlineEdit(project, 'assignedPm')}
                      title="Click to edit">

                                <span className="text-sm text-foreground">
                                  {project?.assignedPm?.nickname || project?.assignedPm?.fullName || project?.assignedPm?.email || <span className="text-muted-foreground italic text-xs">—</span>}
                                </span>
                                <Icon name="Pencil" size={12} className="text-muted-foreground opacity-0 group-hover/cell:opacity-100 transition-opacity flex-shrink-0" />
                              </div>)

                    }
                          {col?.key === 'status' && (
                    editingCell?.projectId === project?.id && editingCell?.field === 'status' ?
                    <div className="flex items-center gap-1">
                                <select
                        ref={editInputRef}
                        value={editingValue}
                        onChange={(e) => setEditingValue(e?.target?.value)}
                        onBlur={() => handleInlineSave(project, 'status')}
                        onKeyDown={(e) => handleInlineKeyDown(e, project, 'status')}
                        className="text-sm text-foreground bg-background border border-accent rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-accent/30">

                                  <option value="Planning">Planning</option>
                                  <option value="Bidding">Bidding</option>
                                  <option value="In Progress">In Progress</option>
                                  <option value="On Hold">On Hold</option>
                                  <option value="Completed">Completed</option>
                                  <option value="Cancelled">Cancelled</option>
                                </select>
                                {savingCell === `${project?.id}-status` &&
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-accent flex-shrink-0" />
                      }
                              </div> :

                    <div
                      className="flex items-center gap-1 group/cell cursor-pointer"
                      onClick={() => handleInlineEdit(project, 'status')}
                      title="Click to edit">

                                <ProjectStatusBadge status={project?.status} />
                                <Icon name="Pencil" size={12} className="text-muted-foreground opacity-0 group-hover/cell:opacity-100 transition-opacity flex-shrink-0" />
                              </div>)

                    }
                          {col?.key === 'progress' &&
                    <div className="w-32">
                              <ProjectProgressBar percentage={project?.completionPercentage} size="sm" />
                            </div>
                    }
                          {col?.key === 'budget' && (
                    editingCell?.projectId === project?.id && editingCell?.field === 'budget' ?
                    <div className="flex items-center gap-1">
                                <input
                        ref={editInputRef}
                        type="number"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e?.target?.value)}
                        onBlur={() => handleInlineSave(project, 'budget')}
                        onKeyDown={(e) => handleInlineKeyDown(e, project, 'budget')}
                        className="text-sm font-medium text-foreground bg-background border border-accent rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-accent/30 w-32"
                        min="0"
                        step="1000" />

                                {savingCell === `${project?.id}-budget` &&
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-accent flex-shrink-0" />
                      }
                              </div> :

                    <div
                      className="flex items-center gap-1 group/cell cursor-pointer"
                      onClick={() => handleInlineEdit(project, 'budget')}
                      title="Click to edit">

                                <span className="text-sm font-medium text-foreground data-text">{formatCurrency(project?.totalBudget)}</span>
                                <Icon name="Pencil" size={12} className="text-muted-foreground opacity-0 group-hover/cell:opacity-100 transition-opacity flex-shrink-0" />
                              </div>)

                    }
                          {col?.key === 'startDate' && (
                    editingCell?.projectId === project?.id && editingCell?.field === 'startDate' ?
                    <div className="flex items-center gap-1">
                                <input
                        ref={editInputRef}
                        type="date"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e?.target?.value)}
                        onBlur={() => handleInlineSave(project, 'startDate')}
                        onKeyDown={(e) => handleInlineKeyDown(e, project, 'startDate')}
                        className="text-sm text-foreground bg-background border border-accent rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-accent/30" />

                                {savingCell === `${project?.id}-startDate` &&
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-accent flex-shrink-0" />
                      }
                              </div> :

                    <div
                      className="flex items-center gap-1 group/cell cursor-pointer"
                      onClick={() => handleInlineEdit(project, 'startDate')}
                      title="Click to edit">

                                <span className="text-sm text-muted-foreground data-text">{formatDate(project?.startDate)}</span>
                                <Icon name="Pencil" size={12} className="text-muted-foreground opacity-0 group-hover/cell:opacity-100 transition-opacity flex-shrink-0" />
                              </div>)

                    }
                          {col?.key === 'actions' &&
                    <div className="flex items-center gap-2">
                              <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/project-detail-management/${project?.id}`)}
                        iconName="FolderOpen"
                        iconPosition="left">

                                Manage
                              </Button>
                              <button
onClick={(e) => { if (e?.shiftKey) setArchiveConfirm(project); }}
                        className="p-1.5 rounded text-muted-foreground hover:text-amber-600 hover:bg-amber-500/10 transition-colors opacity-0 group-hover:opacity-100"
title="Shift+Click to archive project">

                                  <Icon name="Archive" size={15} />
                                </button>
                              {isAdmin &&
                      <button
onClick={(e) => { if (e?.shiftKey) setDeleteConfirm(project); }}
                        className="p-1.5 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
title="Shift+Click to delete project">

                                  <Icon name="Trash2" size={15} />
                                </button>
                      }
                            </div>
                    }
                        </td>
                  )}
                    </tr>
                ) :

                <tr>
                    <td colSpan={activeColumns?.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="FolderOpen" size={48} className="text-muted-foreground" />
                        <p className="text-base text-muted-foreground">No projects match your filters</p>
                        {hasActiveFilters &&
                      <Button variant="outline" size="sm" onClick={handleClearFilters}>Clear Filters</Button>
                      }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {!loading && !error && filteredAndSortedProjects?.length > 0 && (
            <div className="hidden lg:flex items-center justify-between px-6 py-3 border-t border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e?.target?.value)); setCurrentPage(1); }}
                  className="px-2 py-1 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent min-w-[80px]"
                >
                  <option value={20}>20</option>
                  <option value={40}>40</option>
                  <option value={60}>60</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredAndSortedProjects?.length)} of {filteredAndSortedProjects?.length}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-border bg-background hover:bg-muted/50 text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <span className="text-sm font-medium text-foreground">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-border bg-background hover:bg-muted/50 text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Mobile Cards */}
          <div className="lg:hidden p-3 space-y-2">
            {loading ?
            <div className="flex flex-col items-center gap-3 py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                <p className="text-sm text-muted-foreground">Loading projects...</p>
              </div> :
            error ?
            <div className="flex flex-col items-center gap-3 py-12">
                <Icon name="AlertCircle" size={48} className="text-red-500" />
                <p className="text-base text-muted-foreground">{error}</p>
                {hasActiveFilters &&
              <Button variant="outline" size="sm" onClick={handleClearFilters}>Clear Filters</Button>
              }
              </div> :
            paginatedProjects?.length > 0 ?
            paginatedProjects?.map((project) =>
            <div key={project?.id} className="bg-background rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        {project?.convertedFromBid &&
                    <Icon name="FileText" size={16} className="text-accent" />
                    }
                        <span className="text-xs font-medium text-muted-foreground data-text">{project?.projectNumber}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground mb-0.5">{project?.projectName}</h3>
                      <p className="text-xs text-muted-foreground">{project?.customerName}</p>
                      {projectPresence?.[project?.id]?.length > 0 &&
                        <div className="mt-1">
                          <PresenceBadges
                            presenceList={projectPresence?.[project?.id]}
                            currentUserId={userProfile?.id}
                            label="viewing"
                          />
                        </div>
                      }
                    </div>
                    <ProjectStatusBadge status={project?.status} />
                  </div>
                  <ProjectProgressBar percentage={project?.completionPercentage} />
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Budget</p>
                      <p className="text-xs font-semibold text-foreground data-text">{formatCurrency(project?.totalBudget)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Start Date</p>
                      <p className="text-xs font-medium text-foreground data-text">{formatDate(project?.startDate)}</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <Button variant="default" size="xs" onClick={() => navigate(`/project-detail-management/${project?.id}`)} iconName="FolderOpen" iconPosition="left">Manage Project</Button>
                  </div>
                </div>
            ) :

            <div className="flex flex-col items-center gap-3 py-12">
                <Icon name="FolderOpen" size={48} className="text-muted-foreground" />
                <p className="text-base text-muted-foreground">No projects match your filters</p>
                {hasActiveFilters &&
              <Button variant="outline" size="sm" onClick={handleClearFilters}>Clear Filters</Button>
              }
              </div>
            }
            {/* Mobile Pagination */}
            {!loading && !error && filteredAndSortedProjects?.length > 0 && (
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e?.target?.value)); setCurrentPage(1); }}
                    className="px-2 py-1 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none min-w-[80px]"
                  >
                    <option value={20}>20</option>
                    <option value={40}>40</option>
                    <option value={60}>60</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredAndSortedProjects?.length)} of {filteredAndSortedProjects?.length}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded border border-border bg-background text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="ChevronLeft" size={14} />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded border border-border bg-background text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="ChevronRight" size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedProject &&
        <ProjectDetailModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onUpdate={handleUpdateProject}
          onDelete={handleDeleteProject}
          onEdit={handleEditProject}
          onArchived={() => { setSelectedProject(null); loadProjects(); }} />

        }

        {showCreateModal &&
        <CreateProjectModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setConvertingBid(null);
            setEditingProject(null);
          }}
          onSubmit={handleCreateProject}
          onUpdate={handleUpdateProject}
          convertFromBid={convertingBid}
          editProject={editingProject} />

        }

        {/* Delete Confirmation Dialog */}
        {deleteConfirm &&
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => !deleting && setDeleteConfirm(null)} />
            <div className="relative bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Trash2" size={20} className="text-red-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Delete Project</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-6">
                Are you sure you want to delete{' '}
                <span className="font-semibold">{deleteConfirm?.projectName}</span>
                {deleteConfirm?.projectNumber &&
              <span className="text-muted-foreground"> ({deleteConfirm?.projectNumber})</span>
              }
                ? All associated data will be permanently removed.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50">

                  Cancel
                </button>
                <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50">

                  {deleting ?
                <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                      Deleting...
                    </> :

                <>
                      <Icon name="Trash2" size={14} />
                      Delete Project
                    </>
                }
                </button>
              </div>
            </div>
          </div>
        }

        {/* Archive Confirmation Dialog */}
        {archiveConfirm &&
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => !archiving && (setArchiveConfirm(null), setArchiveUnassignResources(false))} />
            <div className="relative bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Archive" size={20} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Archive Project</h3>
                  <p className="text-sm text-muted-foreground">The project will be moved to the archive</p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-4">
                Are you sure you want to archive{' '}
                <span className="font-semibold">{archiveConfirm?.projectName}</span>
                {archiveConfirm?.projectNumber &&
              <span className="text-muted-foreground"> ({archiveConfirm?.projectNumber})</span>
              }
                ? You can restore it from the Archive section at any time.
              </p>
              <label className="flex items-start gap-3 mb-6 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={archiveUnassignResources}
                  onChange={(e) => setArchiveUnassignResources(e?.target?.checked)}
                  disabled={archiving}
                  className="mt-0.5 w-4 h-4 rounded border-border text-accent accent-amber-600 cursor-pointer disabled:opacity-50"
                />
                <div>
                  <span className="text-sm font-medium text-foreground group-hover:text-foreground/80">Unassign all resources</span>
                  <p className="text-xs text-muted-foreground mt-0.5">Remove all resource assignments from this project when archiving</p>
                </div>
              </label>
              <div className="flex gap-3 justify-end">
                <button
                onClick={() => { setArchiveConfirm(null); setArchiveUnassignResources(false); }}
                disabled={archiving}
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50">

                  Cancel
                </button>
                <button
                onClick={handleConfirmArchive}
                disabled={archiving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50">

                  {archiving ?
                <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                      Archiving...
                    </> :

                <>
                      <Icon name="Archive" size={14} />
                      Archive Project
                    </>
                }
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    </MainLayout>
  );

 };

export default ProjectManagement;